import { useState, useMemo } from "react";
import { num, toCm, ml, m2, money, sv, svPrice, CalcLayout, FormCol, Card, Field, Sel, HowTo, Advanced, AreaTable, ResultCard } from "./shared";

const DESP_REC = { "Recta": 5, "Estándar": 10, "Irregular": 12, "Diagonal": 15, "Espiga (Herringbone)": 18, "Chevron": 20 };

export default function Recubrimientos({ onSave, s }) {
  const [u, setU] = useState("m");
  const [tipo, setTipo] = useState("Piso laminado");
  const [instal, setInstal] = useState("Estándar");
  const [areaTipo, setAreaTipo] = useState("Piso");
  const [areas, setAreas] = useState([{ nombre: "Área 1", largo: "5", ancho: "4", cant: "1" }]);
  const [descuentos, setDescuentos] = useState([]);
  const [despMode, setDespMode] = useState("auto");
  const [despManual, setDespManual] = useState(sv(s, "desperdicio_default"));
  const [venta, setVenta] = useState("caja");
  const [coberturaCaja, setCoberturaCaja] = useState("2.20");
  const [piezaAncho, setPiezaAncho] = useState("0.60");
  const [piezaLargo, setPiezaLargo] = useState("2.80");
  const [adhesivoRend, setAdhesivoRend] = useState(sv(s, "adhesivo_rend"));
  const [precio, setPrecio] = useState(svPrice(s, "precio_recubrimiento_m2"));
  const [precioInst, setPrecioInst] = useState(svPrice(s, "precio_instalacion"));

  const toM = (v) => toCm(v, u) / 100;
  const despPct = despMode === "auto" ? (DESP_REC[instal] ?? 10) : num(despManual);

  const r = useMemo(() => {
    const areaTotal = areas.reduce((sum, a) => sum + toM(a.largo) * toM(a.ancho) * Math.max(1, Math.round(num(a.cant)) || 1), 0);
    const areaDesc = descuentos.reduce((sum, d) => sum + toM(d.largo) * toM(d.ancho) * Math.max(1, Math.round(num(d.cant)) || 1), 0);
    const areaNeta = Math.max(0, areaTotal - areaDesc);
    const material = areaNeta * (1 + despPct / 100);
    let unidades = 0, unidadLabel = "", vendido = material, coberturaPieza = 0;
    if (venta === "caja") { const c = num(coberturaCaja); unidades = c > 0 && material > 0 ? Math.ceil(material / c) : 0; vendido = unidades * c; unidadLabel = "Cajas requeridas"; }
    else if (venta === "pieza") { coberturaPieza = toM(piezaAncho) * toM(piezaLargo); unidades = coberturaPieza > 0 && material > 0 ? Math.ceil(material / coberturaPieza) : 0; vendido = unidades * coberturaPieza; unidadLabel = "Piezas requeridas"; }
    const sobrante = vendido - material;
    const aprov = vendido > 0 ? (areaNeta / vendido) * 100 : 0;
    const adhesivo = num(adhesivoRend) > 0 ? Math.ceil(material / num(adhesivoRend)) : 0;
    const bajoPiso = tipo === "Piso laminado" || tipo === "Piso vinílico SPC" ? material : 0;
    const perim = areas.reduce((sum, a) => sum + 2 * (toM(a.largo) + toM(a.ancho)) * Math.max(1, Math.round(num(a.cant)) || 1), 0);
    const zoclos = areaTipo === "Piso" ? perim * (1 + despPct / 100) : 0;
    const pr = num(precio);
    const cMat = venta === "m2" ? material * pr : unidades * pr;
    const total = cMat + num(precioInst);
    const w = [];
    if (despMode === "manual" && num(despManual) < (DESP_REC[instal] ?? 10)) w.push(`El desperdicio (${num(despManual)}%) es menor al recomendado para instalación ${instal} (${DESP_REC[instal] ?? 10}%).`);
    if (venta !== "m2" && vendido > material) w.push(`Se compran ${vendido.toFixed(2)} m² por unidades completas: ${sobrante.toFixed(2)} m² de sobrante.`);
    if (areaNeta === 0) w.push("Captura al menos un área con largo y ancho.");
    const rows = [
      { section: "Proyecto" },
      { label: "Tipo de producto", value: tipo }, { label: "Instalación", value: instal }, { label: "Superficie", value: areaTipo },
      { label: "Número de áreas", value: `${areas.length}` },
      { section: "Áreas" },
      { label: "Área total", value: m2(areaTotal) },
      { label: "Área descontada", value: m2(areaDesc) },
      { label: "Área neta", value: m2(areaNeta) },
      { section: "Material" },
      { label: "Desperdicio aplicado", value: `${despPct}%` },
      { label: "Material requerido", value: m2(material), strong: venta === "m2" },
      ...(venta === "caja" ? [{ label: "Cobertura por caja", value: m2(num(coberturaCaja)) }, { label: unidadLabel, value: `${unidades} caja(s)`, strong: true }] : []),
      ...(venta === "pieza" ? [{ label: "Cobertura por pieza", value: m2(coberturaPieza) }, { label: unidadLabel, value: `${unidades} pieza(s)`, strong: true }] : []),
      { label: "Material comprado", value: m2(vendido) },
      { label: "Material sobrante", value: m2(sobrante) },
      { label: "Aprovechamiento", value: `${aprov.toFixed(0)}%` },
      { section: "Complementos" },
      { label: "Adhesivo (sacos)", value: `${adhesivo}` },
      ...(bajoPiso ? [{ label: "Bajo piso", value: m2(bajoPiso) }] : []),
      ...(zoclos ? [{ label: "Zoclos", value: ml(zoclos) }] : []),
      { section: "Costos" },
      { label: "Materiales", value: money(cMat) },
      { label: "Instalación", value: money(num(precioInst)) },
      { label: "Total estimado", value: money(total), strong: true },
    ];
    const inputs = { u, tipo, instal, areaTipo, venta };
    return { rows, warnings: w, total, inputs };
  }, [u, tipo, instal, areaTipo, areas, descuentos, despMode, despManual, despPct, venta, coberturaCaja, piezaAncho, piezaLargo, adhesivoRend, precio, precioInst]);

  return (
    <CalcLayout>
      <FormCol>
        <HowTo title="¿Cómo cuantificar piso, loseta, lambrín o recubrimiento?">
          <p>Suma el área de cada superficie, resta puertas/ventanas, aplica el desperdicio según el patrón de instalación y convierte a cajas, paneles o piezas (redondeo hacia arriba).</p>
        </HowTo>
        <Card>
          <Sel label="Unidad" value={u} onChange={setU} opts={[["mm", "Milímetros"], ["cm", "Centímetros"], ["m", "Metros"]]} testid="rec-unidad" />
          <Sel label="Tipo de producto" value={tipo} onChange={setTipo} opts={["Piso laminado", "Piso vinílico SPC", "Piso de ingeniería", "Piso de madera", "Loseta cerámica", "Porcelanato", "Mármol", "Granito", "Piedra natural", "Lambrín PVC", "Lambrín WPC", "Panel decorativo", "Panel 3D", "Deck", "Madera tecnológica", "Fachaletas", "Mosaicos", "Azulejos"]} testid="rec-tipo" />
          <Sel label="Tipo de instalación" value={instal} onChange={setInstal} opts={["Recta", "Estándar", "Irregular", "Diagonal", "Espiga (Herringbone)", "Chevron"]} testid="rec-instal" />
          <Sel label="Superficie" value={areaTipo} onChange={setAreaTipo} opts={["Piso", "Muro", "Techo", "Fachada", "Escaleras"]} testid="rec-areatipo" />
        </Card>
        <AreaTable title="Áreas a cubrir (largo × ancho)" rows={areas} setRows={setAreas} u={u} defaultName="Área" testid="rec-areas" />
        <AreaTable title="Descontar (puertas/ventanas)" rows={descuentos} setRows={setDescuentos} u={u} addLabel="+ Descuento" defaultName="Puerta" testid="rec-desc" />
        <Card>
          <Sel label="Desperdicio" value={despMode} onChange={setDespMode} opts={[["auto", `Automático (${DESP_REC[instal] ?? 10}%)`], ["manual", "Manual"]]} testid="rec-despmode" />
          {despMode === "manual" && <Sel label="Porcentaje" value={despManual} onChange={setDespManual} opts={[["5", "5%"], ["10", "10%"], ["12", "12%"], ["15", "15%"], ["18", "18%"], ["20", "20%"]]} testid="rec-despmanual" />}
          <Sel label="Se vende por" value={venta} onChange={setVenta} opts={[["m2", "Metro cuadrado"], ["caja", "Caja / paquete"], ["pieza", "Pieza / panel / tabla"]]} testid="rec-venta" />
          {venta === "caja" && <Field label="Cobertura por caja (m²)" value={coberturaCaja} onChange={setCoberturaCaja} suffix="m²" testid="rec-coberturacaja" />}
          {venta === "pieza" && <><Field label={`Ancho pieza (${u})`} value={piezaAncho} onChange={setPiezaAncho} suffix={u} testid="rec-piezaancho" /><Field label={`Largo pieza (${u})`} value={piezaLargo} onChange={setPiezaLargo} suffix={u} testid="rec-piezalargo" /></>}
        </Card>
        <Advanced>
          <Field label="Rendimiento adhesivo (m²/saco)" value={adhesivoRend} onChange={setAdhesivoRend} step="0.5" suffix="m²" testid="rec-adhesivo" />
          <Field label={`Precio ${venta === "m2" ? "por m²" : venta === "caja" ? "por caja" : "por pieza"} ($)`} value={precio} onChange={setPrecio} suffix="$" testid="rec-precio" />
          <Field label="Instalación ($)" value={precioInst} onChange={setPrecioInst} suffix="$" testid="rec-precio-inst" />
        </Advanced>
      </FormCol>
      <ResultCard title="Cotización de Recubrimiento — GOÛT" modality="recubrimientos" rows={r.rows} warnings={r.warnings} total={r.total} inputs={r.inputs} onSave={onSave} />
    </CalcLayout>
  );
}
